import { useMemo } from "react";
import { type NavigateFunction, useNavigate } from "react-router-dom";
import type { ImmutableObject } from "@/lib/immutable";
import { storefrontSdk } from "@/storefrontsdk";
import { createStore, type Store, useStore } from "./store";
import {
	type Catalog,
	type CatalogState,
	useCatalogState,
} from "./useCatalogState";
import {
	type CheckoutFormState,
	useCheckoutFormState,
} from "./useCheckoutFormState";
import type { Product } from "./useInventoryState";
import { type SiteLayoutState, useSiteLayoutState } from "./useSiteLayoutState";

// state data
export class ProductDetailsModel {
	product?: Product;
	catalog?: Catalog;
	isLoadingProductDetails: boolean = false;
	displayingImageNo?: number = 0;
	showPopup: boolean = false;
}

// make this a global state
const productDetailsStateStore = createStore<ProductDetailsModel>(
	new ProductDetailsModel(),
);

// expose methods to get and modify the state
export class ProductDetailsState {
	private productDetailsState: Store<ProductDetailsModel>;
	private checkoutFormState: CheckoutFormState;
	private catalogState: CatalogState;
	private siteLayoutState: SiteLayoutState;
	private navigate: NavigateFunction;

	constructor(
		productDetailsState: Store<ProductDetailsModel>,
		checkoutFormState: CheckoutFormState,
		catalogState: CatalogState,
		siteLayoutState: SiteLayoutState,
		navigate: NavigateFunction,
	) {
		this.productDetailsState = productDetailsState;
		this.checkoutFormState = checkoutFormState;
		this.catalogState = catalogState;
		this.siteLayoutState = siteLayoutState;
		this.navigate = navigate;
	}

	get(): ImmutableObject<ProductDetailsModel> {
		return this.productDetailsState.getState();
	}

	async setProductIdAsync(
		productid: string,
		warehouseid?: string,
		locationid?: string,
		fromDate?: string,
		toDate?: string,
	): Promise<void> {
		this.productDetailsState.setState((prev) => ({
			...prev,
			isLoadingProductDetails: true,
		}));
		try {
			warehouseid =
				warehouseid ?? this.checkoutFormState.get().warehouseIdValue;
			locationid = locationid ?? this.checkoutFormState.get().locationIdValue;
			fromDate = fromDate ?? this.checkoutFormState.get().fromDateValue;
			toDate = toDate ?? this.checkoutFormState.get().toDateValue;

			// Check if we have the required parameters
			if (!warehouseid || !locationid) {
				console.warn(
					"Missing warehouse or location ID for product details request",
				);
				this.productDetailsState.setState((prev) => ({
					...prev,
					isLoadingProductDetails: false,
				}));
				return;
			}

			const product = (
				await storefrontSdk.storefrontV1GetProductAsync(
					productid,
					warehouseid,
					locationid,
					fromDate,
					toDate,
				)
			).data as unknown as Product;
			this.setProductAsync(product);
		} catch (error) {
			console.error("Error loading product details:", error);
			this.siteLayoutState.showError(error);
		} finally {
			this.productDetailsState.setState((prev) => ({
				...prev,
				isLoadingProductDetails: false,
			}));
		}
	}

	async setProductAsync(product?: Product): Promise<void> {
		if (product) {
			let catalogs = this.catalogState.get().catalogs ?? [];
			let catalog: Catalog | undefined;
			if (catalogs.length > 0) {
				let catalogIndex = catalogs.findIndex(
					(c) => c.Id === product.WebCatalogId,
				);
				if (catalogIndex === -1) {
					await this.catalogState.loadAsync();
					catalogIndex = catalogs.findIndex(
						(c) => c.Id === product.WebCatalogId,
					);
				}
				if (catalogIndex !== -1) {
					catalog = catalogs[catalogIndex];
				}
			}
			if (!catalog) {
				await this.catalogState.loadAsync();
				catalogs = this.catalogState.get().catalogs ?? [];
				const catalogIndex = catalogs.findIndex(
					(c) => c.Id === product.WebCatalogId,
				);
				if (catalogIndex !== -1) {
					catalog = catalogs[catalogIndex];
				}
			}
			if (catalog) {
				this.productDetailsState.setState((prev) => ({ ...prev, catalog }));
			}
		}

		this.productDetailsState.setState((prev) => ({
			...prev,
			product,
			displayingImageNo: 0,
		}));
	}

	setShowPopup(isVisible: boolean): void {
		this.productDetailsState.setState((prev) => ({
			...prev,
			showPopup: isVisible,
		}));
		if (!isVisible) {
			if (window.location.hash.startsWith("/storefront/product/")) {
				this.navigate(-1);
			}
			if (window.location.pathname.startsWith("/storefront/product/")) {
				this.navigate(-1);
			}
		}
	}

	showNextImage(): void {
		const currentImageNo =
			this.productDetailsState.getState().displayingImageNo ?? 0;
		const totalImages =
			this.productDetailsState.getState().product?.Images?.length ?? 0;
		if (currentImageNo < totalImages) {
			this.productDetailsState.setState((prev) => ({
				...prev,
				displayingImageNo: currentImageNo + 1,
			}));
		}
	}

	showPrevImage(): void {
		const currentImageNo =
			this.productDetailsState.getState().displayingImageNo ?? 0;
		if (currentImageNo > 0) {
			this.productDetailsState.setState((prev) => ({
				...prev,
				displayingImageNo: currentImageNo - 1,
			}));
		}
	}

	hasProductDetailsPrerequisites(): boolean {
		const quoteRequestForm = this.checkoutFormState.get();
		return !!(
			quoteRequestForm.warehouseIdValue &&
			quoteRequestForm.fromDateValue &&
			quoteRequestForm.toDateValue
		);
	}
}

// useState function for consumers to use
export function useProductDetailsState(): ProductDetailsState {
	const quoteRequestFormState = useCheckoutFormState();
	const catalogState = useCatalogState();
	const siteLayoutState = useSiteLayoutState();
	const navigate = useNavigate();
	useStore(productDetailsStateStore);
	return useMemo(
		() =>
			new ProductDetailsState(
				productDetailsStateStore,
				quoteRequestFormState,
				catalogState,
				siteLayoutState,
				navigate,
			),
		[quoteRequestFormState, catalogState, siteLayoutState, navigate],
	);
}
